import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback, useRef } from "react";
import { loginRequest } from "../config/authConfig";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://localhost:7001";
const RATE_LIMIT = {
  maxRequests: parseInt(
    process.env.REACT_APP_RATE_LIMIT_MAX_REQUESTS || "100",
    10
  ),
  timeWindow: parseInt(
    process.env.REACT_APP_RATE_LIMIT_TIME_WINDOW || "60000",
    10
  ), // 1 minute
};

export const useApi = () => {
  const { instance } = useMsal();
  const requestCountRef = useRef(0);
  const lastResetTimeRef = useRef(Date.now());

  const resetRateLimit = () => {
    const now = Date.now();
    if (now - lastResetTimeRef.current >= RATE_LIMIT.timeWindow) {
      requestCountRef.current = 0;
      lastResetTimeRef.current = now;
    }
  };

  const checkRateLimit = () => {
    resetRateLimit();
    if (requestCountRef.current >= RATE_LIMIT.maxRequests) {
      throw new Error("Rate limit exceeded. Please try again later.");
    }
    requestCountRef.current++;
  };

  const callApi = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      checkRateLimit();

      const account = instance.getActiveAccount();
      if (!account) {
        // Do not trigger redirect here; let the route guard handle it to avoid double prompts
        throw new Error("Not authenticated");
      }

      try {
        // Try to get token silently
        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account,
          });
        } catch (error) {
          console.error("Silent token acquisition failed:", error);
          if (error instanceof InteractionRequiredAuthError) {
            // Let UI/route guard initiate a single interactive login
            throw new Error("Interaction required");
          }
          throw error;
        }

        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          Accept: "application/json",
          "X-Content-Type-Options": "nosniff",
          "X-Frame-Options": "DENY",
          "X-XSS-Protection": "1; mode=block",
          "Strict-Transport-Security": "max-age=31536000; includeSubDomains",
          ...(!(options.body instanceof FormData) && {
            "Content-Type": "application/json",
          }),
        };

        const response = await fetch(`${API_BASE_URL}${url}`, {
          ...options,
          headers: {
            ...headers,
            ...options.headers,
          },
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            // If we can't parse the error response as JSON, use the status text
            errorMessage = response.statusText || errorMessage;
          }

          if (response.status === 401) {
            // Bubble up so caller can handle auth refresh centrally
            throw new Error("Unauthorized");
          }
          throw new Error(errorMessage);
        }

        return response;
      } catch (error) {
        console.error("API call failed:", error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error("An unexpected error occurred");
      }
    },
    [instance]
  );

  return { callApi };
};
